<template>
  <div class="realtimeMin" :class="{ realtime: screenSize > 1200 }">
    <div class="topchartMin" :class="{ topchart: screenSize > 1200 }">
      <div class="chartBox" :class="{ chartBoxMin: screenSize < 1200 }" ref="speed"></div>
      <div class="chartBox" :class="{ chartBoxMin: screenSize < 1200 }" ref="power"></div>
      <div
        class="chartBox"
        :class="{ chartBoxMin: screenSize < 1200 }"
        ref="pressure"
      ></div>
    </div>
    <div class="line">
      <div class="infocontent">
        <div class="infoitems">
          <div class="infotitle">{{ $t("index.height") }}</div>
          <div class="infonumber" :title="devicein.height">{{ devicein.height }}</div>
        </div>
        <div class="infoitems">
          <div class="infotitle">{{ $t("index.percent") }}</div>
          <div class="infonumber" :title="devicein.percent">{{ devicein.percent }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { onMounted, onUnmounted } from "vue";
import * as echarts from "echarts";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const screenSize = ref();
const width = window.innerWidth;
screenSize.value = width;
const props = defineProps({
  devicein: { type: Object, required: true },
  speed: { type: Number, required: true },
  power: { type: Number, required: true },
  pressure: { type: Number, required: true },
});

const realData = ref({
  speed: props.speed,
  power: props.power,
  pressure: props.pressure,
});

let speed = ref(null);
let power = ref(null);
let pressure = ref(null);

let myEcharts = echarts;

onMounted(() => {
  if (realData.value) {
    initSpeedChart();
    initPowerChart();
    initPressureChart();
  }
});

onUnmounted(() => {
  myEcharts.dispose;
});

function initSpeedChart() {
  let chart = myEcharts.init(speed.value, null, {
    renderer: "svg",
  });
  let option;
  let mainColor = "#fac858";

  option = {
    series: [
      {
        name: "Indicator",
        type: "gauge",
        data: [
          {
            value: realData.value.speed,
            name: `${t("index.speed")}(m/min)`,
            itemStyle: { color: mainColor },
            title: {
              show: true,
              offsetCenter: ["0", "100%"],
            },
          },
        ],
        detail: {
          show: true,
        },
        max: 1000,
        splitNumber: 10,
        progress: {
          show: true,
          itemStyle: {
            color: mainColor,
          },
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: [[1, "rgba(250, 200, 88, 0.2)"]],
          },
        },
      },
    ],
  };
  option && chart.setOption(option);
  window.onresize = function () {
    chart.resize();
  };
}

function initPowerChart() {
  let chart = myEcharts.init(power.value, null, {
    renderer: "svg",
  });
  let option;
  let mainColor = "#0064c8";

  option = {
    series: [
      {
        name: "Indicator",
        type: "gauge",
        data: [
          {
            value: realData.value.power,
            name: `${t("index.power")}(kW)`,
            itemStyle: { color: mainColor },
            title: {
              show: true,
              offsetCenter: ["0", "100%"],
            },
          },
        ],
        detail: {
          show: true,
        },
        max: 60,
        splitNumber: 10,
        progress: {
          show: true,
          itemStyle: {
            color: mainColor,
          },
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: [[1, "rgba(84, 112, 198, 0.2)"]],
          },
        },
      },
    ],
  };
  option && chart.setOption(option);
  window.onresize = function () {
    chart.resize();
  };
}

function initPressureChart() {
  let chart = myEcharts.init(pressure.value, null, {
    renderer: "svg",
  });
  let option;
  let mainColor = "#50aa50";

  option = {
    series: [
      {
        name: "Indicator",
        type: "gauge",
        data: [
          {
            value: realData.value.pressure,
            name: `${t("index.voltage")}(V)`,
            itemStyle: { color: mainColor },
            title: {
              show: true,
              offsetCenter: ["0", "100%"],
            },
          },
        ],
        detail: {
          show: true,
        },
        max: 10000,
        splitNumber: 10,
        progress: {
          show: true,
          itemStyle: {
            color: mainColor,
          },
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: [[1, "rgba(80, 170, 80, 0.2)"]],
          },
        },
      },
    ],
  };
  option && chart.setOption(option);
  window.onresize = function () {
    chart.resize();
  };
}
</script>

<style scoped>
.realtime {
  width: 100%;
  height: 380px;
}
.realtimeMin {
  width: 100%;
  justify-content: space-between;
}
.topchart {
  width: 100%;
  display: flex;
  justify-content: space-between;
  height: 293px;
}
.topchartMin {
}
.chartBox {
  width: 33%;
  height: 100%;
}
.chartBoxMin {
  width: 100% !important;
  height: 310px !important;
}
.line {
  width: 100%;
  display: flex;
}
.infocontent {
  flex-grow: 1;
  display: flex;
  flex-wrap: nowrap;
  position: relative;
  box-sizing: border-box;
  align-items: center;
}
.infoitems {
  border-color: #c0c6cc;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16);
  height: 80px;
  position: relative;
  text-align: left;
  border-radius: 4px;
  padding: 12px;
  width: 48%;
  text-decoration: none;
  background: #f7f9fa;
  border: 1px solid transparent;
  transition: all 0.1s linear;
  margin-right: 10px;
}
.infoitems:hover {
  border-color: #c0c6cc;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16);
}
.infotitle {
  line-height: 18px;
  height: 18px;
  color: #555;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 18px;
  height: 18px;
}
.infonumber {
  display: block;
  line-height: 50px;
  height: 50px;
  font-size: 18px;
  font-weight: 500;
  color: #333;
  width: 100%;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}
</style>
